import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
// @mui
import { Box } from '@mui/material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
//
import { COMPANY } from '../config/company';
import { CONTAINER, FONT, STAGE, TYPE } from '../theme/stage';
import { srOnly } from '../sections/home/a11y';
import { CONTACT } from '../sections/home/copy';
import ActionLink from '../sections/home/primitives/ActionLink';
import Eyebrow from '../sections/home/primitives/Eyebrow';
import PageShell from '../sections/home/PageShell';
import Rich from '../sections/home/primitives/Rich';

// ----------------------------------------------------------------------

const COPIED_FOR = 2000;

const HOVER = '@media (hover: hover)';

const rule = `1px solid ${STAGE.line}`;

const TEXT = {
  m: '16px 0 0',
  fontFamily: FONT.sans,
  fontSize: 17,
  lineHeight: '26px',
  color: STAGE.ink2,
  textWrap: 'pretty',
};

const ACTION = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  minHeight: 44,
  p: 0,
  border: 0,
  bgcolor: 'transparent',
  fontFamily: FONT.sans,
  fontWeight: 500,
  fontSize: { xs: 16, sm: 17 },
  lineHeight: 1,
  color: STAGE.ink2,
  cursor: 'pointer',
  '& .label': { textUnderlineOffset: '4px' },
  '& svg': { fontSize: 18 },
  '&:disabled': { color: STAGE.mute, cursor: 'default' },
  [HOVER]: { '&:enabled:hover .label': { textDecoration: 'underline' } },
  '@media print': { display: 'none' },
};

type CardProps = {
  title: string;
  body: string;
  children: ReactNode;
};

function Card({ title, body, children }: CardProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        p: { xs: '28px 24px', sm: '40px' },
        border: rule,
        borderRadius: '24px',
        bgcolor: STAGE.surface,
      }}
    >
      <Box component="h2" sx={{ ...TYPE.label, m: 0, color: STAGE.mute }}>
        {title}
      </Box>
      <Box component="p" sx={TEXT}>
        {body}
      </Box>
      {children}
    </Box>
  );
}

function CopyAddress({ email, target }: { email: string | null; target: RefObject<HTMLElement> }) {
  const [copiedAt, setCopiedAt] = useState(0);

  const [selectedAt, setSelectedAt] = useState(0);

  const copied = copiedAt > 0;

  useEffect(() => {
    if (!copiedAt && !selectedAt) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setCopiedAt(0);
      setSelectedAt(0);
    }, COPIED_FOR);
    return () => window.clearTimeout(timer);
  }, [copiedAt, selectedAt]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      window.getSelection()?.removeAllRanges();
      setSelectedAt(0);
      setCopiedAt(Date.now());
    } catch {
      const selection = window.getSelection();
      if (target.current && selection) {
        const range = document.createRange();
        range.selectNodeContents(target.current);
        selection.removeAllRanges();
        selection.addRange(range);
        setSelectedAt(Date.now());
      }
    }
  };

  const Icon = copied ? CheckRounded : ContentCopyRounded;

  return (
    <>
      <Box
        component="button"
        type="button"
        disabled={!email}
        onClick={() => email && copy(email)}
        sx={ACTION}
      >
        <Icon aria-hidden />
        <span className="label">{copied ? CONTACT.general.copied : CONTACT.general.copy}</span>
      </Box>
      <Box component="span" role="status" sx={srOnly}>
        {copied ? CONTACT.general.copied : selectedAt > 0 && CONTACT.general.selected}
      </Box>
    </>
  );
}

export default function ContactPage() {
  const addressRef = useRef<HTMLParagraphElement>(null);

  return (
    <PageShell head={CONTACT.head}>
      <Box
        component="section"
        aria-labelledby={CONTACT.titleId}
        sx={{
          ...CONTAINER,
          pt: { xs: '104px', sm: '160px' },
          pb: { xs: '96px', sm: '128px' },
          '@media print': {
            p: 0,
            '&, & *': {
              color: '#000000 !important',
              borderColor: '#8A8A8A !important',
              bgcolor: 'transparent !important',
            },
          },
        }}
      >
        <Eyebrow>{CONTACT.eyebrow}</Eyebrow>
        <Box
          component="h1"
          id={CONTACT.titleId}
          sx={{
            m: 0,
            fontFamily: FONT.sans,
            fontWeight: 500,
            fontSize: { xs: 48, sm: 72, lg: 96 },
            lineHeight: 1.02,
            letterSpacing: { xs: '-0.03em', sm: '-0.035em', lg: '-0.04em' },
            color: STAGE.ink,
          }}
        >
          <Rich parts={CONTACT.title} />
        </Box>
        <Box
          component="p"
          sx={{ ...TYPE.lead, m: '20px 0 0', maxWidth: '30em', color: STAGE.ink2 }}
        >
          {CONTACT.lead}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
            gap: { xs: '16px', md: '24px' },
            mt: { xs: '48px', sm: '64px' },
          }}
        >
          <Card title={CONTACT.general.title} body={CONTACT.general.body}>
            <Box
              ref={addressRef}
              component="p"
              sx={{
                m: '28px 0 0',
                fontFamily: FONT.sans,
                fontWeight: 500,
                fontSize: COMPANY.email ? { xs: 24, sm: 32 } : 18,
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                color: STAGE.ink,
                overflowWrap: 'anywhere',
              }}
            >
              <Rich parts={[{ field: 'email' }]} />
            </Box>
            <Box sx={{ mt: 'auto', pt: '28px' }}>
              <CopyAddress email={COMPANY.email} target={addressRef} />
            </Box>
          </Card>

          <Card title={CONTACT.support.title} body={CONTACT.support.body}>
            <Box sx={{ mt: 'auto', pt: '28px' }}>
              <ActionLink variant="link" href={CONTACT.support.link.href}>
                {CONTACT.support.link.label}
              </ActionLink>
            </Box>
          </Card>
        </Box>

        <Box
          component="p"
          sx={{
            m: '40px 0 0',
            pt: '20px',
            borderTop: rule,
            fontFamily: FONT.mono,
            fontSize: 13,
            lineHeight: '22px',
            letterSpacing: '.02em',
            color: STAGE.mute,
          }}
        >
          <Rich parts={CONTACT.details} />
        </Box>
      </Box>
    </PageShell>
  );
}
